/* eslint-disable react-refresh/only-export-components */
import { Fragment, type ReactNode } from "react";

// A deliberately tiny formatter for help copy. We avoid pulling in a
// markdown dependency: help text only ever needs **bold**, `code`,
// paragraph breaks (blank line) and "- " bullet lists. Anything richer
// belongs in the structured fields (steps, concepts, faqs), not prose.

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

/** Render **bold** and `code` spans inside a single line of text. */
export function formatInline(text: string): ReactNode[] {
  const parts = text.split(INLINE).filter((p) => p !== "");
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-riot-navy-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="rounded bg-riot-navy-100 px-1 py-0.5 font-mono text-[0.85em] text-riot-navy-800 dark:bg-riot-navy-800 dark:text-riot-navy-100"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

interface Block {
  type: "p" | "ul";
  lines: string[];
}

function toBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  for (const raw of text.split(/\n{2,}/)) {
    const chunk = raw.trim();
    if (!chunk) continue;
    const lines = chunk.split("\n").map((l) => l.trim());
    if (lines.every((l) => l.startsWith("- "))) {
      blocks.push({ type: "ul", lines: lines.map((l) => l.slice(2)) });
    } else {
      blocks.push({ type: "p", lines });
    }
  }
  return blocks;
}

/** Render a block of help prose (paragraphs + simple bullet lists). */
export function RichText({ text, className }: { text: string; className?: string }) {
  const blocks = toBlocks(text);
  return (
    <div className={className}>
      {blocks.map((block, i) =>
        block.type === "ul" ? (
          <ul key={i} className="my-2 ml-4 list-disc space-y-1 text-sm text-riot-navy-600 dark:text-riot-navy-300">
            {block.lines.map((line, j) => (
              <li key={j}>{formatInline(line)}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="my-2 text-sm leading-relaxed text-riot-navy-600 dark:text-riot-navy-300">
            {block.lines.map((line, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {formatInline(line)}
              </Fragment>
            ))}
          </p>
        ),
      )}
    </div>
  );
}
